import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Footer, Sub, Table } from '../../performance-operations/styles';
import Button from '../../../components/ui/Button';
import { UNIT_PANEL_ROWS, fmt, hasNum, signed } from '../config';

// Analiz §14 E11 alt alan — birim trend tablosu (ilk ve son dönem değeri, değişim, dahil/hariç kişi) ve kapsam değişikliği
// açıklaması. Panelde en kalabalık 6 birim; tamamı "Birimler" sekmesinde (DataTable). Gizli hücre "Gizli" yazar, değişimi de.
const Facts = styled.ul`
  margin: 12px 0 0;
  padding-left: 18px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.6;
`;

export function unitValue(v, t, lang) {
  if (!v) return '-';
  if (v.suppressed) return t('performance.trends.suppressed');
  return hasNum(v.value) ? fmt(v.value, lang) : '-';
}

export default function UnitTrendPanel({ panel, summary, onSeeAll }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const units = (panel.data?.units || []).slice(0, UNIT_PANEL_ROWS);
  const scope = summary?.scope;
  const unitLabel = (u) => u.label || t('performance.trends.unassigned');
  return (
    <PanelCard
      panelKey="units" title={t('performance.trends.panel.units')} span={7}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={units.length === 0}
      actions={onSeeAll ? <Button variant="ghost" onClick={onSeeAll} data-link="units">{t('performance.trends.seeAllUnits')}</Button> : null}
    >
      <Table>
        <thead>
          <tr>
            <th>{t('performance.trends.col.unit')}</th>
            <th>{t('performance.trends.col.first')}</th>
            <th>{t('performance.trends.col.last')}</th>
            <th>{t('performance.trends.col.change')}</th>
            <th>{t('performance.trends.col.goalChange')}</th>
            <th>{t('performance.trends.col.included')}</th>
            <th>{t('performance.trends.col.excluded')}</th>
          </tr>
        </thead>
        <tbody>
          {units.map((u) => {
            const first = u.values[0];
            const last = u.values[u.values.length - 1];
            return (
              <tr key={u.key || 'none'} data-row={u.key || 'none'}>
                <td title={unitLabel(u)}>{unitLabel(u)}</td>
                <td>{unitValue(first, t, lang)}</td>
                <td>{unitValue(last, t, lang)}</td>
                <td>{u.changeSuppressed ? t('performance.trends.suppressed') : (hasNum(u.change) ? signed(u.change, lang) : '-')}</td>
                <td>{u.changeSuppressed ? t('performance.trends.suppressed') : (hasNum(u.goalChange) ? signed(u.goalChange, lang) : '-')}</td>
                <td>{u.includedN}</td>
                <td>{u.excludedN}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {panel.data?.orgView === 'current' ? <Sub style={{ marginTop: 8 }}>{t('performance.trends.currentOrgRegrouped')}</Sub> : null}
      {scope ? (
        <Facts data-scope="1">
          <li>{t('performance.trends.scope.population', { first: scope.populationFirst, last: scope.populationLast })}</li>
          {summary.mode === 'cohort' ? <li>{t('performance.trends.scope.cohort', { n: scope.cohortN })}</li> : null}
          <li>{t('performance.trends.scope.joinedLeft', { joined: scope.joined, left: scope.left })}</li>
          {scope.roleChanged > 0 ? <li>{t(summary.sameRole ? 'performance.trends.scope.roleExcluded' : 'performance.trends.scope.roleChanged', { count: scope.roleChanged })}</li> : null}
          {scope.noFinalInCohort > 0 ? <li>{t('performance.trends.scope.noFinal', { count: scope.noFinalInCohort })}</li> : null}
          {scope.excludedOrLeftLast > 0 ? <li>{t('performance.trends.scope.excluded', { count: scope.excludedOrLeftLast })}</li> : null}
          {scope.backfilledSnapshots > 0 ? <li>{t('performance.trends.scope.backfilled', { count: scope.backfilledSnapshots })}</li> : null}
        </Facts>
      ) : null}
      {panel.data?.truncated ? <Footer><Sub>{t('performance.trends.unitsTruncated')}</Sub></Footer> : null}
    </PanelCard>
  );
}
